import { isEmpty } from "lodash";
import React from "react";
import { InsGridAggs } from "../../widgets/grid/grid-agg-funcs";
import { ClickthroughLinkOverride } from "./clickthroughLinkOverride";
import { InspectDataComparisonTab, InspectDataTab } from "./types";
import { DatapointFormatEnum, DatapointIdStandardEnum } from "api/ux/src";
import { formatToColumnTypes, getCustomColumnTypes } from "components/widgets/grid/grid-column-types";
import { CustomColumnRendererMappingArray } from "components/widgets/grid/renderers";
import { excludedDatapointsForInspectData } from "components/widgets/hooks/use-inspect-data";
import { isFundOpsModeEnabled, useTenantSettings } from "hooks/use-tenant-settings";
import { language } from "language/default";
import { RelativeValueOptions } from "store/widget-workshop/types";
const NonQuantitativeTypes = [formatToColumnTypes.string, formatToColumnTypes.entity, formatToColumnTypes.date, formatToColumnTypes.performancePeriod];
const getColumnType = (datapoint, datapointMetadata) => {
    if (datapoint?.relativeValueOption &&
        (datapoint?.relativeValueOption === RelativeValueOptions.AgainstGrandTotal || datapoint?.relativeValueOption === RelativeValueOptions.AgainstGroups)) {
        return formatToColumnTypes.percent;
    }
    return formatToColumnTypes[datapointMetadata?.metadata?.format ? datapointMetadata?.metadata?.format : "string"];
};
export const ClickthroughLinkCellRenderer = (e) => {
    const columnTypeDefinition = getCustomColumnTypes("");
    let defaultRendererContent = "";
    const rendererName = columnTypeDefinition[e.column.colDef.type]?.cellRenderer;
    const formatter = columnTypeDefinition[e.column.colDef.type]?.valueFormatter;
    const [tenantSettings] = useTenantSettings();
    const { clickthroughStartDate } = tenantSettings;
    if (rendererName && CustomColumnRendererMappingArray[rendererName]) {
        defaultRendererContent = CustomColumnRendererMappingArray[rendererName](e);
    }
    else if (formatter) {
        defaultRendererContent = formatter(e);
    }
    else {
        defaultRendererContent = e.value;
    }
    const fieldName = (e?.column?.colDef?.field).replaceAll(".", "$");
    const documentIdKey = e.data && Object.keys(e?.data)?.find((a) => a === `clickthrough-${fieldName}`);
    if (documentIdKey && clickthroughStartDate && e.data?.ReferenceDate &&
        new Date(e.data.ReferenceDate).getTime() >= new Date(clickthroughStartDate).getTime() && e.data?.[documentIdKey]) {
        return React.createElement(ClickthroughLinkOverride, { documentId: e.data[documentIdKey], defaultRendererContent: defaultRendererContent, fundMasterId: e.data.fundMasterId, datapointTarget: e.column.colDef.name, assetMasterId: e.data.assetMasterId });
    }
    return React.createElement(React.Fragment, null, defaultRendererContent);
};
const getColumnLabel = (datapoint, datapointMetadata, type) => {
    const label = datapointMetadata?.metadata?.label?.toString();
    if (datapoint.context?.objective) {
        return `Pacing ${label}`;
    }
    if (datapoint.context?.projectedOnly && NonQuantitativeTypes.every((t) => t !== type)) {
        return `${label} (Projected)`;
    }
    return label;
};
const normalizeFormat = (format) => {
    if (!format) {
        return undefined;
    }
    const normalized = format.toString();
    const variants = [normalized, normalized.toLowerCase(), normalized.toUpperCase()];
    for (const variant of variants) {
        if (formatToColumnTypes[variant]) {
            return variant;
        }
    }
    return undefined;
};
const inferFormulaColumnType = (formula, format, datapoints, metadata) => {
    const { operator, left } = formula;
    const normalizedFormat = normalizeFormat(format);
    if (normalizedFormat) {
        return formatToColumnTypes[normalizedFormat];
    }
    if (operator === "/") {
        return formatToColumnTypes.percent;
    }
    if (operator === "+" || operator === "-") {
        const leftOperand = datapoints.find((d) => d.name === left);
        if (leftOperand) {
            const leftMetadata = metadata.find((md) => md.id?.toUpperCase() === leftOperand.standardDatapoint?.toString().toUpperCase() ||
                (leftOperand.customDatapoint?.toString() && md.id?.toString().toUpperCase() === leftOperand.customDatapoint?.toString().toUpperCase()));
            if (leftMetadata?.metadata?.format) {
                return formatToColumnTypes[leftMetadata.metadata.format] || formatToColumnTypes.number;
            }
        }
    }
    return formatToColumnTypes.number;
};
export const getColumDef = (datapoint, datapointMetadata) => {
    const type = getColumnType(datapoint, datapointMetadata);
    const label = getColumnLabel(datapoint, datapointMetadata, type);
    const aggregationFunction = InsGridAggs.totalData;
    if (datapoint.format === DatapointFormatEnum.Variation) {
        return { children: [
                { aggFunc: aggregationFunction,
                    cellRenderer: "ClickthroughLinkCellRenderer",
                    field: `${datapoint.name}.currentValue`,
                    headerName: "Current",
                    name: datapoint.standardDatapoint,
                    type },
                { aggFunc: aggregationFunction,
                    cellRenderer: "ClickthroughLinkCellRenderer",
                    columnGroupShow: "open",
                    field: `${datapoint.name}.previousValue`,
                    headerName: "Previous",
                    name: datapoint.standardDatapoint,
                    type },
                { aggFunc: aggregationFunction,
                    columnGroupShow: "open",
                    field: `${datapoint.name}.change`,
                    headerName: "Variation",
                    type },
                { aggFunc: aggregationFunction,
                    columnGroupShow: "open",
                    field: `${datapoint.name}.changePercent`,
                    headerName: "Variation (%)",
                    hide: datapointMetadata?.metadata?.format === "esg",
                    type: formatToColumnTypes.percent }
            ],
            field: datapoint.name,
            groupId: `${datapoint.name}.group`,
            headerName: label };
    }
    return { aggFunc: aggregationFunction,
        cellRenderer: "ClickthroughLinkCellRenderer",
        field: datapoint.name,
        headerName: label,
        name: datapoint.standardDatapoint,
        type };
};
const findDatapointMetadata = (dp, metadata) => metadata.find((md) => {
    const standardMatch = md.id?.toUpperCase() === dp.standardDatapoint?.toString().toUpperCase();
    const customMatch = dp.customDatapoint?.toString() &&
        md.id?.toString().toUpperCase() === dp.customDatapoint?.toString().toUpperCase();
    return standardMatch || customMatch;
});
const processRegularDatapoint = (dp, dpMetadata) => {
    if (!dpMetadata || (!dp.standardDatapoint?.toString() && !dp.customDatapoint?.toString())) {
        return null;
    }
    return getColumDef(dp, dpMetadata);
};
const processFormulaDatapoint = (dp, datapoints, metadata) => {
    if (!dp.context?.formula) {
        return null;
    }
    const { formula, format } = dp.context;
    const formulaLabel = dp.name || "Formula";
    const inferredType = inferFormulaColumnType(formula, format, datapoints, metadata);
    return {
        aggFunc: InsGridAggs.totalData,
        field: dp.name,
        headerName: formulaLabel,
        name: dp.standardDatapoint,
        type: inferredType
    };
};
const separateAndSortColumns = (columns) => {
    const measureValuableColDefs = [];
    const measureNonValuableColDefs = columns.filter((a) => {
        if (NonQuantitativeTypes.some((t) => t.toString() === a.type)) {
            return true;
        }
        measureValuableColDefs.push(a);
        return false;
    });
    if (measureNonValuableColDefs.length > 0) {
        measureNonValuableColDefs[0].sort = "asc";
    }
    const dateRelatedColDefs = [{ field: "ReferenceDate", headerName: language.en.modal.inspectData.details.referenceDate, sort: "desc", type: formatToColumnTypes.date }];
    return measureNonValuableColDefs.concat(dateRelatedColDefs, measureValuableColDefs);
};
const shouldExcludeDatapoint = (dp) => {
    if (!isFundOpsModeEnabled() &&
        (dp.standardDatapoint === DatapointIdStandardEnum.FundFundOpsPaidInAmount ||
            dp.standardDatapoint === DatapointIdStandardEnum.FundFundOpsPaidOutAmount)) {
        return true;
    }
    return Boolean(dp.standardDatapoint && excludedDatapointsForInspectData.indexOf(dp.standardDatapoint) !== -1);
};
const processDatapoint = (dp, dpMetadata, datapoints, metadata) => {
    // Handle formula datapoints which may not have metadata
    if (dp.context?.formula && !dpMetadata) {
        return processFormulaDatapoint(dp, datapoints, metadata);
    }
    // Handle regular datapoints
    return processRegularDatapoint(dp, dpMetadata);
};
export const getColumnDefs = (datapoints, metadata) => {
    const columns = [];
    if (datapoints.length === 0) {
        return columns;
    }
    datapoints.forEach((dp) => {
        if (shouldExcludeDatapoint(dp)) {
            return;
        }
        const dpMetadata = findDatapointMetadata(dp, metadata);
        const colDef = processDatapoint(dp, dpMetadata, datapoints, metadata);
        if (colDef) {
            columns.push(colDef);
        }
    });
    return separateAndSortColumns(columns);
};
const tabsLang = language.en.modal.inspectData.tabs;
// Lower number means higher priority in tab list
const tabWidgetMap = {
    ActualsAndProjections: [
        { priority: 7, tab: { label: language.en.modal.inspectData.tabs.InspectData, uid: InspectDataTab.FullHistory } },
        { priority: 8, tab: { label: language.en.modal.inspectData.tabs.ActualsAndProjections, uid: InspectDataTab.ActualsAndProjections } },
        { priority: 9, tab: { label: language.en.modal.inspectData.tabs.CommitmentPacingOutputs, uid: InspectDataTab.CommitmentPacingOutputs } }
    ],
    Asset: [{ priority: 2, tab: { label: tabsLang.Asset, uid: InspectDataTab.Asset } }],
    Cashflows: [{ priority: 3, tab: { label: tabsLang.Cashflows, uid: InspectDataTab.Cashflows } }],
    ComparisonPortfolio: [
        { priority: 5, tab: { label: tabsLang.PortfolioA, uid: InspectDataComparisonTab.MainPortfolio } },
        { priority: 6, tab: { label: tabsLang.PortfolioB, uid: InspectDataComparisonTab.ComparatePortfolio } }
    ],
    Fund: [{ priority: 1, tab: { label: tabsLang.Fund, uid: InspectDataTab.Fund } }],
    Portfolio: [{ priority: 4, tab: { label: tabsLang.Portfolio, uid: InspectDataTab.Portfolio } }]
};
export const createTabList = (widgets) => {
    const tabs = [];
    Object.keys(tabWidgetMap).forEach((key) => {
        if (!isEmpty(widgets[key]?.componentProps?.datapointParams)) {
            tabs.push(...tabWidgetMap[key]);
        }
    });
    if (tabs.length !== 0 && isEmpty(widgets.Portfolio.componentProps?.datapointParams)) {
        tabs.pushIfNotExist({ priority: 10, tab: { label: tabsLang.FullHistory, uid: InspectDataTab.FullHistory } }, (x) => x.tab.uid === InspectDataTab.FullHistory);
    }
    tabs.sort((x, y) => x.priority - y.priority);
    return tabs.map((x) => x.tab);
};
export const openOnce = (url, windowName) => {
    let winref = window.open("", windowName, "");
    try {
        if (winref?.location.href === "about:blank" || (winref?.location?.href && winref.location.pathname !== "/clickthrough")) {
            winref.location.href = url;
        }
    }
    catch {
        winref?.close();
        winref = window.open(url, windowName);
    }
    return winref;
};
